import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wallet, Plus, X, Copy, Check, Bitcoin, Coins, Sparkles } from 'lucide-react';

interface WalletData {
  id: string;
  address: string;
  name: string;
  type: 'ETH' | 'BTC';
  balance: number;
  usdValue: number;
}

interface WalletManagerProps {
  wallets: WalletData[];
  onAddWallet: (wallet: Omit<WalletData, 'id'>) => void;
  onRemoveWallet: (id: string) => void;
}

const WalletManager: React.FC<WalletManagerProps> = ({ wallets, onAddWallet, onRemoveWallet }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    address: '',
    name: '',
    type: 'ETH' as 'ETH' | 'BTC',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.address && formData.name) {
      // Generate mock balance data
      const mockBalance = formData.type === 'ETH' ? Math.random() * 10 : Math.random() * 2;
      const mockUsdValue = formData.type === 'ETH' ? mockBalance * 2500 : mockBalance * 45000;
      
      onAddWallet({
        ...formData,
        balance: mockBalance,
        usdValue: mockUsdValue,
      });
      setFormData({ address: '', name: '', type: 'ETH' });
      setShowAddForm(false);
    }
  };

  const copyAddress = (id: string, address: string) => {
    navigator.clipboard.writeText(address);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getWalletIcon = (type: 'ETH' | 'BTC') => {
    return type === 'BTC' ? Bitcoin : Coins;
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-zinc-900/90 to-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-xl p-6 relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-violet-600/10 rounded-full blur-3xl" />
      
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Wallet className="w-5 h-5 text-violet-400" />
            My Wallets
            <Sparkles className="w-4 h-4 text-amber-400" />
          </h2>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowAddForm(true)}
            className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-all shadow-lg shadow-violet-600/25"
          >
            <Plus className="w-4 h-4" />
            Add Wallet
          </motion.button>
        </div>

      <AnimatePresence>
        {showAddForm && (
          <motion.form
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            onSubmit={handleSubmit}
            className="mb-6 overflow-hidden"
          >
            <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Wallet Type
                </label>
                <div className="flex gap-2">
                  {['ETH', 'BTC'].map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setFormData({ ...formData, type: type as 'ETH' | 'BTC' })}
                      className={`flex-1 px-4 py-2 rounded-lg border transition-all ${
                        formData.type === type
                          ? 'bg-violet-600 border-violet-600 text-white'
                          : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        {React.createElement(getWalletIcon(type as 'ETH' | 'BTC'), { className: 'w-4 h-4' })}
                        {type}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Wallet Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Main Wallet"
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2 text-white placeholder-zinc-500 focus:border-violet-500 focus:outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Wallet Address
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder={formData.type === 'ETH' ? '0x...' : 'bc1...'}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2 text-white placeholder-zinc-500 focus:border-violet-500 focus:outline-none transition-colors font-mono text-sm"
                />
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="flex-1 bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Add Wallet
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      <div className="space-y-3">
        {wallets.length === 0 ? (
          <div className="text-center py-8 text-zinc-500">
            No wallets added yet. Click "Add Wallet" to get started.
          </div>
        ) : (
          wallets.map((wallet) => {
            const Icon = getWalletIcon(wallet.type);
            return (
              <motion.div
                key={wallet.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                whileHover={{ scale: 1.02 }}
                className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 hover:border-violet-600/50 transition-all group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      wallet.type === 'BTC' ? 'bg-orange-500/20' : 'bg-violet-500/20'
                    }`}>
                      <Icon className={`w-5 h-5 ${
                        wallet.type === 'BTC' ? 'text-orange-400' : 'text-violet-400'
                      }`} />
                    </div>
                    <div>
                      <div className="font-medium text-white">{wallet.name}</div>
                      <div className="flex items-center gap-2 text-sm text-zinc-400">
                        <span className="font-mono">
                          {wallet.address.slice(0, 6)}...{wallet.address.slice(-4)}
                        </span>
                        <button
                          onClick={() => copyAddress(wallet.id, wallet.address)}
                          className="hover:text-zinc-300 transition-colors"
                        >
                          {copiedId === wallet.id ? (
                            <Check className="w-3 h-3 text-green-400" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-white font-medium">
                        {wallet.balance.toFixed(4)} {wallet.type}
                      </div>
                      <div className="text-sm text-zinc-400">
                        ${wallet.usdValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                    <button
                      onClick={() => onRemoveWallet(wallet.id)}
                      className="text-zinc-500 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
      </div>
      </div>
    </motion.div>
  );
};

export default WalletManager;